/**
 * LivretCompletCelebration.jsx — Célébration finale fullscreen quand l'élève
 * termine son livret pédagogique Parcours AM (36 fiches acquises).
 *
 * Contenu :
 *  - Feu d'artifice confettis continu (via canvas-confetti)
 *  - Carte souvenir personnalisée (nom élève + date + numéro de séquence)
 *  - CTA : Télécharger le PDF + Retour au Hub
 *  - Fermeture manuelle (le bouton X reste toujours accessible)
 *
 * Idempotent : ne s'ouvre qu'une seule fois par élève / livret (localStorage).
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, Trophy, FileDown, Home, Sparkles, Award, PartyPopper, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export default function LivretCompletCelebration({
  open,
  onClose,
  eleveName = 'Élève',
  livretTitle = 'Livret pédagogique Permis AM',
  totalFiches = 36,
  onDownloadPdf,
  pdfLoading = false,
  hubPath = '/permis-am/voiturette',
  ceremoniePath = '/permis-am/ceremonie',
  accentColor = '#f43f5e',
}) {
  const canvasRef = useRef(null);

  // Feu d'artifice continu tant que le modal est ouvert
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let cleanup;
    import('canvas-confetti').then(({ default: confetti }) => {
      if (cancelled) return;
      const colors = ['#fbbf24', '#f59e0b', '#facc15', '#10b981', '#22d3ee', '#f43f5e', '#ffffff'];
      // Salve initiale géante
      confetti({
        particleCount: 220,
        spread: 130,
        origin: { y: 0.35 },
        colors,
        scalar: 1.2,
      });
      confetti({
        particleCount: 100,
        spread: 60,
        origin: { y: 0.15, x: 0.3 },
        colors,
      });
      confetti({
        particleCount: 100,
        spread: 60,
        origin: { y: 0.15, x: 0.7 },
        colors,
      });
      // Feu d'artifice latéral continu
      const interval = setInterval(() => {
        if (cancelled) return;
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 60,
          startVelocity: 55,
          origin: { x: 0, y: 0.9 },
          colors,
        });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 60,
          startVelocity: 55,
          origin: { x: 1, y: 0.9 },
          colors,
        });
      }, 350);
      cleanup = () => clearInterval(interval);
    }).catch(() => { /* fallback silencieux */ });
    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [open]);

  const now = new Date();
  const dateStr = useMemo(
    () => now.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  if (!open) return null;

  const displayName = (eleveName || 'Élève').trim() || 'Élève';

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center p-3 sm:p-6"
      data-testid="livret-complet-celebration"
      style={{
        background:
          'radial-gradient(ellipse at top, rgba(244,63,94,0.25) 0%, rgba(15,23,42,0.98) 60%, rgba(0,0,0,0.98) 100%)',
      }}
    >
      {/* Fermeture accessible (mais pas au premier plan) */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white/85 flex items-center justify-center backdrop-blur transition z-20"
        aria-label="Fermer la célébration"
        data-testid="celebration-close"
      >
        <X size={20} />
      </button>

      {/* Anneaux décoratifs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-20 -left-20 w-96 h-96 rounded-full opacity-25 blur-3xl"
          style={{ background: accentColor }}
        />
        <div
          className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{ background: '#fbbf24' }}
        />
      </div>

      {/* Contenu central */}
      <div className="relative z-10 max-w-2xl w-full text-center">
        {/* Badge Trophy animé */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-500/30 to-amber-500/10 border border-amber-500/50 text-amber-200 text-[10px] font-black uppercase tracking-[0.28em] mb-4 animate-pulse">
          <Sparkles className="w-3.5 h-3.5" />
          Formation validée
          <Sparkles className="w-3.5 h-3.5" />
        </div>

        {/* Titre monumentale */}
        <h1
          className="text-4xl sm:text-6xl font-black text-white leading-[0.95] mb-4"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            textShadow: '0 4px 20px rgba(244,63,94,0.35)',
          }}
        >
          <span className="block">Bravo</span>
          <span
            className="block bg-gradient-to-r from-amber-300 via-rose-400 to-amber-300 bg-clip-text text-transparent"
            data-testid="celebration-name"
          >
            {displayName} !
          </span>
        </h1>

        <p className="text-base sm:text-lg text-white/80 max-w-md mx-auto leading-snug mb-6">
          Tu as validé <strong className="text-white">{totalFiches} fiches sur {totalFiches}</strong> —
          l'intégralité du <em className="text-white/90 not-italic">{livretTitle}</em>.
          Ta persévérance paye. 🎯
        </p>

        {/* Carte souvenir */}
        <div
          className="relative mx-auto max-w-md rounded-3xl overflow-hidden mb-6 shadow-2xl"
          style={{
            background:
              'linear-gradient(135deg, #f8fafc 0%, #fee2e2 40%, #fef3c7 70%, #f8fafc 100%)',
            boxShadow: `0 24px 60px rgba(244,63,94,0.35), 0 0 0 3px ${accentColor}55`,
          }}
        >
          {/* Bandeau */}
          <div
            className="px-4 py-2 text-white text-[9.5px] font-black uppercase tracking-[0.28em] flex items-center justify-between"
            style={{ background: `linear-gradient(90deg, ${accentColor}, #f59e0b)` }}
          >
            <span className="flex items-center gap-1.5">
              <Award className="w-3 h-3" /> Carte souvenir · Parcours AM
            </span>
            <span>Code 2 Roues</span>
          </div>
          {/* Corps carte */}
          <div className="p-6 text-slate-900 text-left">
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-white flex-shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${accentColor}, #f59e0b)`,
                  boxShadow: `0 8px 24px ${accentColor}44`,
                }}
              >
                <Trophy className="w-8 h-8" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-black uppercase tracking-[0.22em] text-rose-600 mb-0.5">
                  Décerné à
                </div>
                <div
                  className="text-2xl font-black leading-tight truncate"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {displayName}
                </div>
                <div className="text-[11px] text-slate-500 italic mt-0.5">
                  Livret pédagogique complété · {dateStr}
                </div>
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-slate-300/50 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-2xl font-black text-emerald-600">100%</div>
                <div className="text-[8.5px] font-black uppercase tracking-widest text-slate-500">Progression</div>
              </div>
              <div>
                <div className="text-2xl font-black text-rose-600">{totalFiches}</div>
                <div className="text-[8.5px] font-black uppercase tracking-widest text-slate-500">Fiches</div>
              </div>
              <div>
                <div className="text-2xl font-black text-amber-600">5/5</div>
                <div className="text-[8.5px] font-black uppercase tracking-widest text-slate-500">Séquences</div>
              </div>
            </div>
          </div>
          {/* Bas de carte */}
          <div className="px-4 py-2 bg-slate-900/5 border-t border-slate-300/50 text-[9px] font-black uppercase tracking-widest text-slate-500 text-center">
            Souvenir pédagogique · Non officiel · Attestation délivrée par l'auto-école
          </div>
        </div>

        {/* Actions — hiérarchie : Cérémonie (primaire) → PDF → Retour hub */}
        <div className="flex flex-col gap-3 items-center">
          {/* CTA principal : passer à la cérémonie officielle */}
          <Button
            asChild
            size="lg"
            className="w-full sm:w-auto min-w-[280px] bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-black uppercase tracking-widest text-base shadow-2xl px-6 py-6 h-auto animate-pulse-slow"
            style={{
              boxShadow: '0 0 30px rgba(251,191,36,0.5), 0 10px 40px rgba(251,191,36,0.3)',
              animation: 'celebrationPulse 2.2s ease-in-out infinite',
            }}
            data-testid="celebration-ceremonie-btn"
          >
            <Link to={`${ceremoniePath}?eleve=${encodeURIComponent(eleveName)}&step=carte`}>
              <PartyPopper className="w-5 h-5 mr-2" />
              Passer à la cérémonie officielle
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>

          {/* Actions secondaires */}
          <div className="flex flex-col sm:flex-row gap-2 justify-center flex-wrap">
            {onDownloadPdf && (
              <Button
                onClick={onDownloadPdf}
                disabled={pdfLoading}
                size="sm"
                variant="outline"
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/60 font-bold uppercase tracking-wider text-xs"
                data-testid="celebration-download-pdf"
              >
                <FileDown className="w-3.5 h-3.5 mr-1.5" />
                {pdfLoading ? 'Génération…' : 'Télécharger mon PDF'}
              </Button>
            )}
            <Button
              asChild
              size="sm"
              variant="outline"
              className="border-white/25 text-white/75 hover:bg-white/5 font-bold uppercase tracking-wider text-xs"
              data-testid="celebration-hub-btn"
            >
              <Link to={hubPath}>
                <Home className="w-3.5 h-3.5 mr-1.5" /> Retour au hub
              </Link>
            </Button>
          </div>
        </div>

        <p className="mt-5 text-center text-[11px] text-white/55 italic max-w-md mx-auto leading-relaxed">
          🎉 <strong className="text-amber-300 not-italic">La cérémonie officielle</strong> te fera revivre les 7 grands thèmes du permis AM et tu recevras ta carte souvenir personnalisée avec tes engagements signés.
        </p>

        <style>{`
          @keyframes celebrationPulse {
            0%, 100% { transform: scale(1); box-shadow: 0 0 30px rgba(251,191,36,0.5), 0 10px 40px rgba(251,191,36,0.3); }
            50% { transform: scale(1.03); box-shadow: 0 0 50px rgba(251,191,36,0.7), 0 15px 50px rgba(251,191,36,0.5); }
          }
        `}</style>
      </div>
    </div>
  );
}
